import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import cn from 'classnames';
import { useTranslation, LocalizedContent } from '@framework/i18n';
import { LOCALES } from '@/dealroadshow/application/config/i18n';
import ErrorCodeHelper from '@dealroadshow/error-codes';
import { RpcError } from '@dealroadshow/json-rpc-dispatcher';
import EmailInput from '@framework/ui/Organisms/FinalForm/Fields/EmailInput';
import Input from '@dealroadshow/uikit-input';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import Tooltip from '@dealroadshow/uikit-tooltip';
import Button, { ButtonVariantType } from '@dealroadshow/uikit-button';
import IconQuestion from '@dealroadshow/uikit-icon/IconQuestion';
import FinalForm, { Field, type TFinalFormRenderProp } from '@framework/ui/Organisms/FinalForm';
import stripTags from '@framework/dom/stripTags';
import TermsOfUse from '@/dealroadshow/ui/common/TermsOfUse';
import validate from './validator';
import LoginContext, { useLoginContext } from '@users/session/LoginContext';
import { getSearchParams } from './helpers';

import { namespaces } from '@/dealroadshow/ui/translations/namespaces';
import { INVESTOR_LOGIN_FORM_NAME } from '@/dealroadshow/ui/components/Landing/Institutional/InvestorLogin/constants';

import ja from './locales/ja.json';
import en from './locales/en.json';

import cardStyles from '@dealroadshow/uikit-styles/card.module.scss';
import customStyles from './customStyles.module.scss';
import styles from './investorLoginForm.module.scss';

interface ICustomSettings {
  customMessage?: string | null,
  loginPrimaryText?: string,
  loginSecondaryText?: string,
}

interface IProps {
  isDirectLogin?: boolean,
  entryCode?: string,
  customSettings?: ICustomSettings,
  isCustomizationDisclaimerEnabled?: boolean,
}

interface IFormValues {
  entryCode: string,
  email: string,
}

function InvestorLoginForm({
  isDirectLogin,
  entryCode,
  customSettings = {},
  isCustomizationDisclaimerEnabled,
}: IProps) {
  const { t } = useTranslation(namespaces.INVESTOR_LOGIN_FORM, { en, ja });

  const { loginInvestorDrs } = useLoginContext();
  const router = useRouter();
  const searchParams = getSearchParams(router);

  const [hasCustomMessage, setHasCustomMessage] = useState(false);
  const [initialValues, setInitialValues] = useState<IFormValues>({
    entryCode,
    email: '',
  });

  const onSubmit = async (values: IFormValues) => {
    try {
      await loginInvestorDrs({
        email: values.email,
        entryCode: values.entryCode,
        source: searchParams.source,
      });
    } catch (error) {
      if (
        error instanceof RpcError &&
        [
          ErrorCodeHelper.getCodeByName('INVESTOR_VERIFICATION_EMAIL_SENT'),
          ErrorCodeHelper.getCodeByName('USER_ACTIVATION_EMAIL_SENT'),
        ].includes(error.getCode())
      ) {
        const url = new URL(
          `/login/investor/e/${ entryCode }/email_verification/${ values.email }`,
          window.location.origin,
        );

        if (searchParams.source) {
          url.searchParams.set('source', searchParams.source);
        }

        router.replace(url.toString());
      }
    }
  };

  useEffect(() => {
    const customMessage = customSettings?.customMessage;
    const hasMessage = customMessage != null && !!stripTags(customMessage)?.length;
    setHasCustomMessage(hasMessage);

    if (searchParams.email && searchParams.entryCode) {
      setInitialValues({
        email: searchParams.email,
        entryCode: searchParams.entryCode,
      });
    }
  }, []);

  const getTitle = () => {
    if (isCustomizationDisclaimerEnabled) {
      return customSettings?.loginPrimaryText ?? '';
    }
    if (isDirectLogin && !isCustomizationDisclaimerEnabled) {
      return t('title');
    }
    return t('titleInstitutional');
  };

  const emailLabel = isDirectLogin ? t('email') : t('corporateEmailLabel');

  const renderFields: TFinalFormRenderProp<IFormValues> = ({
    submitting,
    invalid,
  }) => (
    <>
      <Field
        component={ EmailInput }
        inputClassName={ styles.loginFormInput }
        name="email"
        label={ (
          <div className={ cn({ [styles.customLoginForm]: isCustomizationDisclaimerEnabled }) }>
            { emailLabel }
          </div>
        ) }
        dataTest="investorLoginEmailInput"
        autoFocus
        showIcon={ false }
        enableMxValidation
        warningMessage={ t('verifyEmail') }
      />
      <div className={ styles.loginFormEntryCode }>
        <Field
          component={ Input }
          name="entryCode"
          inputClassName={ styles.loginFormInput }
          disabled={ isDirectLogin }
          dataTest="investorLoginEntryCodeInput"
          label={ (
            <div
              className={ cn(styles.loginFormLabel, {
                [styles.customLoginFormLabel]: isCustomizationDisclaimerEnabled,
              }) }
            >
              { t('fieldEntryCodeLabel') }
              <span
                className={ cn(styles.loginFormLabelIcon, {
                  [styles.customLoginFormLabelIcon]: isCustomizationDisclaimerEnabled,
                }) }
              >
                <Tooltip
                  placement="top"
                  content={ t('entryCodeTooltip') }
                >
                  <IconQuestion />
                </Tooltip>
              </span>
            </div>
          ) }
          type="text"
        />
      </div>
      <Button
        type="submit"
        variant={ ButtonVariantType.action }
        className={ cn(styles.loginFormButton, {
          [customStyles.btn]: isCustomizationDisclaimerEnabled,
        }) }
        disabled={ invalid || submitting }
        block
        dataTest="investorLoginFormSubmitButton"
      >
        <>
          { isCustomizationDisclaimerEnabled ? t('customizedSubmitButton') : t('submitButton') }  { ' ' }
          <Spinner
            inline
            size="S"
            color="on-color"
            isVisible={ submitting }
          />
        </>
      </Button>
    </>
  );

  return (
    <div className={ styles.investorLogin }>
      <div
        className={ cn(cardStyles.cardContainer, {
          [customStyles.customCardContainer]: isCustomizationDisclaimerEnabled,
        }) }
      >
        <div className={ cn(styles.investorLoginContainer, {
            [styles.customInvestorLoginContainer]: isCustomizationDisclaimerEnabled,
          }) }
        >
          <h1 className={ styles.title }>
            { getTitle() }
          </h1>
          <div className={ styles.caption }>
            { customSettings?.loginSecondaryText ?? t('loginSecondaryText') }
          </div>
          { hasCustomMessage && (
            <div
              className={ styles.customMessage }
              dangerouslySetInnerHTML={ { __html: customSettings?.customMessage } }
            />
          ) }
          <div className={ styles.loginForm }>
            <FinalForm<IFormValues>
              dataTest={ INVESTOR_LOGIN_FORM_NAME }
              onSubmit={ onSubmit }
              render={ renderFields }
              validate={ (values) => validate({ ...values }, t) }
              initialValues={ initialValues }
            />
          </div>
          <TermsOfUse
            className={ cn(styles.terms, {
              [customStyles.terms]: isCustomizationDisclaimerEnabled,
            }) }
          />
        </div>
        <LocalizedContent language={ LOCALES.EN }>
          <div className={ cn(styles.compatibilityLinkContainer, {
              [customStyles.customCompatibilityLinkContainer]: isCustomizationDisclaimerEnabled,
            }) }
          >
            <a
              href={ isDirectLogin ? `/compatibility?e=${ entryCode }&directLogin=true` : '/compatibility' }
            >
              Trouble viewing a roadshow?
            </a>
          </div>
        </LocalizedContent>
      </div>
    </div>
  );
}

export default ((props: IProps) => (
  <LoginContext>
    <InvestorLoginForm { ...props } />
  </LoginContext>
));
